import {
  EHeadingStyleVariant,
  EModalHeaderTextAlignVariant,
  EModalSize,
  EModalVariants,
  Heading,
  Modal,
} from '@outdoorsyco/bonfire';
import dynamic from 'next/dynamic';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Loading from '@/components/switchback/Loading';
import { EFilterCategories, ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useBringItToMeFilterState } from '@/hooks/useBringItToMeFilter';
import { triggerDeliveryFilterFromAd } from '@/redux/modules/search';
import { ISearchFormFilters } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchFormDeliveryFilter } from '@/redux/selectors/search/searchForm';
import { getIsDeliveryFilterTriggeredFromAd } from '@/redux/selectors/search/searchResults';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

import { EDeliveryFilterStep } from '../FilterPills/FilterPillsContentContainer';
import BringItToMeFilter from '../RentalFilters/BringItToMeFilter/BringItToMeFilter';
import LegacyDeliveryFilter from '../RentalFilters/LegacyDeliveryFilter/LegacyDeliveryFilter';
import { PriceFilterContainer } from '../RentalFilters/PriceFilter/PriceFilterContainer';
import FilterModalContent from './FilterModalContent';

const loading = () => (
  <div className="flex items-center justify-center w-full h-64">
    <Loading />
  </div>
);
loading.displayName = 'loader';

const RVTypeFilter = dynamic(
  () => import('@/components/route/search/RentalFilters/RVTypeFilter/RVTypeFilter'),
  { loading },
);
const RVProfileFilter = dynamic(
  () => import('@/components/route/search/RentalFilters/RVProfileFilter/RVProfileFilter'),
  { loading },
);
const MoreFilter = dynamic(
  () => import('@/components/route/search/RentalFilters/MoreFilter/MoreFilter'),
  { loading },
);
const DriverAgeFilter = dynamic(() => import('./DriverAgeFilter/DriverAgeFilter'), { loading });

interface IFilterFunction {
  onSubmit: () => void;
  onReset: (valuesToRestore?: Partial<ISearchFormFilters>) => void;
  onDismiss: () => void;
}

interface IProps {
  isModalOpen: boolean;
  handleDismiss: () => void;
  filterFunctions: Partial<Record<EFilterCategories, IFilterFunction>>;
  deliveryFilterStep?: EDeliveryFilterStep;
  setDeliveryFilterStep?: (step: EDeliveryFilterStep) => void;
}

const FilterModalContainer: React.FC<IProps> = ({
  isModalOpen,
  handleDismiss,
  filterFunctions,
  deliveryFilterStep = EDeliveryFilterStep.PICKUP,
}) => {
  const intl = useIntl();
  const { isMobile } = useBreakpoint();
  const selectedFilter = useSelector<TRootState, EFilterCategories | null>(
    state => state.search.selectedFilter,
  );
  const queryParams = useSelector(getQueryParams);
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  const isDeliveryFilterTriggeredFromAd = useSelector(getIsDeliveryFilterTriggeredFromAd);
  const dispatch = useDispatch();

  const shouldShowBringItToMeFilter =
    isRentalsVDeliverySearchModeEnabled &&
    !isDeliveryMode &&
    selectedFilter === EFilterCategories.DELIVERY;

  const {
    [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromStore,
    [ESearchFilters.DELIVERY]: isDeliveryFromStore,
    [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromStore,
    [ESearchFilters.STATIONARY_ACKNOWLEDGED]: stationaryAcknowledgedFromStore,
  } = useSelector(getSearchFormDeliveryFilter);

  const { [ESearchFilters.FILTER_RENTER_AGE]: currentRenterAge } = queryParams;

  const {
    shouldDisableBringItToMeButtons,
    handleUserInteraction,
    originalSearchValues,
    storeOriginalSearchValues,
  } = useBringItToMeFilterState(
    isModalOpen,
    shouldShowBringItToMeFilter,
    deliveryFilterStep,
    isDeliveryFromStore,
    deliveryAddressFromStore,
  );

  const shouldDisableDeliverySubmit =
    deliveryStationaryFromStore === EDeliveryOption.STATIONARY && !stationaryAcknowledgedFromStore;

  const onDismissHandler = () => {
    if (selectedFilter) {
      filterFunctions?.[selectedFilter]?.onDismiss();
    }
    handleDismiss();
    isDeliveryFilterTriggeredFromAd && dispatch(triggerDeliveryFilterFromAd(false));
  };

  const onReset = () => {
    if (selectedFilter) {
      if (shouldShowBringItToMeFilter) {
        filterFunctions?.[selectedFilter]?.onReset(originalSearchValues);
      } else {
        filterFunctions?.[selectedFilter]?.onReset();
      }
    }
    handleDismiss();
    isDeliveryFilterTriggeredFromAd && dispatch(triggerDeliveryFilterFromAd(false));
  };

  const onSubmit = () => {
    if (selectedFilter) {
      filterFunctions?.[selectedFilter]?.onSubmit();
    }
    handleDismiss();
    isDeliveryFilterTriggeredFromAd && dispatch(triggerDeliveryFilterFromAd(false));
  };

  const renderFilter = () => {
    let filterComponent;

    switch (selectedFilter) {
      case EFilterCategories.TYPE:
        filterComponent = <RVTypeFilter />;
        break;
      case EFilterCategories.RV_PROFILE:
        filterComponent = <RVProfileFilter />;
        break;
      case EFilterCategories.MORE:
        filterComponent = <MoreFilter />;
        break;
      case EFilterCategories.PRICE:
        filterComponent = <PriceFilterContainer show={isModalOpen} showConfirm={false} />;
        break;
      case EFilterCategories.DELIVERY:
        filterComponent = shouldShowBringItToMeFilter ? (
          <BringItToMeFilter
            onUserInteraction={handleUserInteraction}
            storeOriginalSearchValues={storeOriginalSearchValues}
          />
        ) : (
          <LegacyDeliveryFilter />
        );
        break;
      case EFilterCategories.DRIVER_AGE:
        return (
          <DriverAgeFilter
            onSubmit={onSubmit}
            onReset={onReset}
            currentRenterAge={currentRenterAge ? Number(currentRenterAge) : undefined}
          />
        );
      default:
        return null;
    }
    return (
      <FilterModalContent
        onReset={onReset}
        onSubmit={onSubmit}
        isBringItToMeFilter={shouldShowBringItToMeFilter}
        isSubmitDisabled={
          selectedFilter === EFilterCategories.DELIVERY &&
          (shouldDisableBringItToMeButtons || shouldDisableDeliverySubmit)
        }
        isResetDisabled={
          selectedFilter === EFilterCategories.DELIVERY && shouldDisableBringItToMeButtons
        }>
        {filterComponent}
      </FilterModalContent>
    );
  };

  if (!selectedFilter) {
    return null;
  }

  const titleLabelsMap: Partial<Record<EFilterCategories, string>> = {
    [EFilterCategories.TYPE]: intl.formatMessage({
      defaultMessage: 'RV type',
      id: 'YqHpvR',
      description: 'Filter modal container: title - rv type',
    }),
    [EFilterCategories.RV_PROFILE]: intl.formatMessage({
      defaultMessage: 'RV profile',
      id: 'lfrkzT',
      description: 'Filter modal container: title - rv profile',
    }),
    [EFilterCategories.MORE]: intl.formatMessage({
      defaultMessage: 'All Filters',
      id: 'YmFd2a',
      description: 'Filter modal container: title - more',
    }),
    [EFilterCategories.DELIVERY]: shouldShowBringItToMeFilter
      ? intl.formatMessage({
          defaultMessage: 'Bring it to me',
          id: 'oXUtOi',
          description: 'Filter modal container: title - Bring it to me',
        })
      : intl.formatMessage({
          defaultMessage: 'Getting the RV',
          id: 'WMQGyb',
          description: 'Filter modal container: title - Getting the RV',
        }),
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onDismissHandler}
      size={EModalSize.Medium}
      variant={isMobile ? EModalVariants.Bottom : undefined}
      className="z-[1400]">
      <Modal.Actions />
      <Modal.Header
        textAlign={
          isMobile ? EModalHeaderTextAlignVariant.Left : EModalHeaderTextAlignVariant.Center
        }
        inlineHeader
        className="flex flex-row gap-3">
        <Heading variant={EHeadingStyleVariant.H6}>{titleLabelsMap[selectedFilter]}</Heading>
      </Modal.Header>
      {renderFilter()}
    </Modal>
  );
};

export default FilterModalContainer;
